import { useEffect, useRef, useState } from "react";
import { ConsequenceVisual } from "./ConsequenceVisual";
import type { ConsequenceVisualKind } from "../scenario/types";

/**
 * Small looping wrapper around ConsequenceVisual — the visual's `stage` is
 * normally driven by a Consequence beat's narration progress, but here there
 * is no narration to sync to, so this just free-runs the same stage sequence
 * on a timer (pause → advance → hold → repeat) to keep the modal graphic
 * alive while the learner reads. Stops advancing on unmount.
 */
function LoopingHelperVisual({ kind, maxStage = 2 }: { kind: ConsequenceVisualKind; maxStage?: number }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    let alive = true;
    let s = 0;
    setStage(0);
    const tick = () => {
      if (!alive) return;
      s = s >= maxStage ? 0 : s + 1;
      setStage(s);
    };
    const id = window.setInterval(tick, 1900);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [kind, maxStage]);
  return <ConsequenceVisual kind={kind} stage={stage} />;
}

/**
 * Generic "learn more" helper modal opened from a terminal-debrief recap
 * topic (see TerminalDebrief.tsx) — same two-column glass chrome as
 * BadUsbExplain.tsx's `.bue-*` classes (copy left, illustration right), but
 * the art slot plays a small LOOPING ConsequenceVisual dramatization instead
 * of a static image, and it's dismissable via a close button (it doesn't
 * advance any beat, matching BadUsbInfoModal.tsx's "purely explanatory"
 * behavior) rather than BadUsbExplain's story-advancing single CTA.
 */
export function TerminalHelperModal({
  kicker,
  title,
  lede,
  blocks,
  facts,
  visual,
  onClose,
}: {
  kicker: string;
  title: string;
  lede: string;
  blocks: { h: string; body: string }[];
  facts: string[];
  visual: ConsequenceVisualKind;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="bue-wrap thm-wrap" role="dialog" aria-label={title}>
      <div className="bue-card thm-card">
        <button type="button" className="busb-info-close thm-close" onClick={onClose} aria-label="Close" ref={closeRef}>
          {"\u2715"}
        </button>
        <div className="bue-copy">
          <div className="bue-kicker">{kicker}</div>
          <h2>{title}</h2>
          <p className="bue-lede">{lede}</p>
          {blocks.map((b, i) => (
            <div className="bue-block" key={i}>
              <h3>{b.h}</h3>
              <p>{b.body}</p>
            </div>
          ))}
          <ul className="bue-facts">
            {facts.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
          <button type="button" className="btn bue-continue" onClick={onClose}>
            Got it — close
          </button>
        </div>
        <div className="bue-art thm-art" aria-hidden>
          <LoopingHelperVisual kind={visual} />
        </div>
      </div>
    </div>
  );
}
